import { Action } from '@reduxjs/toolkit';
import { combineEpics, Epic } from 'redux-observable';
import {
  concatMap,
  distinctUntilChanged,
  filter,
  from,
  map,
  skipUntil,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import _, { partial, round } from 'lodash';

import { notNull } from 'utils/type_guards';
import { centerWithinContainer } from 'utils/math_util';

import { selectDefaultPPI, selectZoomedPPI } from 'selectors/ppi';

import rooms from 'domains/wall_preview_rooms';
import canvases from 'domains/canvases';
import Arrangement from 'domains/arrangement';

import module from './index';

import type { RootState } from 'store/wos_store';

const { actions, selectors } = module;

/**
 * Autofit works by adjusting the stage zoom level to fit the arrangement perfectly within the screen
 *
 * Attempt to autofit the arrangement to the screen when:
 * - Adding a canvas
 * - Removing a canvas
 * - Leaving wall preview
 * - Restoring a saved arrangement
 * - When directed by user
 */
export const autofitArrangementEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.autofit.match),
    withLatestFrom(state$),
    map(([, state]) => {
      // Never run this when wall preview is on
      if (selectors.wallPreviewActive(state)) return;

      const defaultPPI = selectDefaultPPI(state);

      // Unlikely we would be running this if default ppi is not ready,
      // but since we're dividing by it we should break early if that's the case
      if (!defaultPPI) return;

      const withProducts = canvases.selectors.allWithProducts(state);
      const arrangement = Arrangement.createWithProducts(withProducts);

      const screenDimensions = selectors.screenDimensions(state);

      const primaryDim = arrangement.orientation === 'landscape' ? 'width' : 'height';
      const secondaryDim = primaryDim === 'width' ? 'height' : 'width';

      let newppi = screenDimensions[primaryDim] / arrangement[primaryDim];

      // If the calculated primary dimension is going to be too large, use the other one
      if (arrangement[secondaryDim] * newppi > screenDimensions[secondaryDim]) {
        newppi = screenDimensions[secondaryDim] / arrangement[secondaryDim];
      }

      // Find the zoom value to fit items on stage, adjust for a little extra margin
      let fitZoom = newppi / defaultPPI;
      fitZoom -= fitZoom * 0.1;

      // Find the new center of the arrangement
      let centerPosition = centerWithinContainer(arrangement, {
        width: screenDimensions.width / (defaultPPI * fitZoom),
        height: screenDimensions.height / (defaultPPI * fitZoom),
      });

      // Round these since the zoom slider input is limited anyways
      centerPosition = {
        x: round(centerPosition.x, 2),
        y: round(centerPosition.y, 2),
      };

      return [
        actions.setStageZoom(fitZoom),
        canvases.actions.setArrangementPosition(centerPosition),
      ];
    }),
    filter(notNull),
    concatMap((a) => from(a))
  );

/**
 * Autofit whenever the default ppi changes -- generally when the product sizes change
 */
export const autofitOnPPIChangeEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    withLatestFrom(state$),
    map(([, state]) => selectDefaultPPI(state)),
    distinctUntilChanged(),
    map(() => actions.autofit())
  );

/**
 * Map wall preview turning off to the autofit action
 */
export const autofitOnWallPreviewOffEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(rooms.actions.unsetWhichWallPreview.match),
    map(() => actions.autofit())
  );

/**
 * Map creating a duplicate canvas to autofit action, unless wall preview is on
 */
export const autofitOnDuplicateCanvasEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(canvases.actions.createCanvas.match),
    filter(({ payload }) => !!payload.duplicate),
    withLatestFrom(state$),
    filter(([, state]) => !selectors.wallPreviewActive(state)),
    map(() => actions.autofit())
  );

/**
 * Map removing a canvas to autofit action, unless wall preview is on
 */
export const autofitOnRemoveCanvasEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(canvases.actions.removeCanvas.match),
    withLatestFrom(state$),
    filter(([, state]) => !selectors.wallPreviewActive(state)),
    map(() => actions.autofit())
  );

/**
 * Center the arrangement on the screen
 */
export const centerArrangementEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.center.match),
    withLatestFrom(state$),
    map(([, state]) => {
      const ppi = selectZoomedPPI(state);
      const screenDimensions = selectors.screenDimensions(state);
      const withProducts = canvases.selectors.allWithProducts(state);
      const arrangement = Arrangement.createWithProducts(withProducts);

      if (
        !ppi ||
        !arrangement.width ||
        !arrangement.height ||
        !screenDimensions.height ||
        !screenDimensions.width
      ) {
        return;
      }

      let centerPosition = centerWithinContainer(arrangement, {
        width: screenDimensions.width / ppi,
        height: screenDimensions.height / ppi,
      });

      return {
        current: canvases.selectors.arrangementPosition(state),
        next: { x: centerPosition.x, y: centerPosition.y },
      };
    }),
    filter(notNull),
    filter(({ current, next }) => {
      const round2 = partial(round, _, 2);
      return round2(current.x) !== round2(next.x) || round2(current.y) !== round2(next.y);
    }),
    map(({ next }) => canvases.actions.setArrangementPosition(next))
  );

/**
 * Center the arrangement when wall preview is toggled on & has the correct size
 */
export const centerOnWallPreviewOnEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(rooms.actions.setWhichWallPreview.match),
    switchMap(() =>
      action$.pipe(
        skipUntil(state$.pipe(filter((state) => selectors.wallPreviewActive(state)))),
        take(1)
      )
    ),
    map(() => actions.center())
  );

/**
 * If the stage zoom level changes, this zooms to its center rather than its top-left
 */
export const centerOnStageZoomEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.setStageZoom.match),
    map(() => actions.center())
  );

export default combineEpics(
  autofitArrangementEpic,
  autofitOnPPIChangeEpic,
  autofitOnWallPreviewOffEpic,
  autofitOnDuplicateCanvasEpic,
  autofitOnRemoveCanvasEpic,
  centerArrangementEpic,
  centerOnWallPreviewOnEpic,
  centerOnStageZoomEpic
);
